import { GlassCard } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { MapPin, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { type InstalledApp } from "@/hooks/useDevices";
import DeviceCardHeader from "./device/DeviceCardHeader";
import DeviceCardInfo from "./device/DeviceCardInfo";
import { toast } from "sonner";
import * as devicesApi from "@/services/devicesApi";

interface Device {
  id: string;
  name: string;
  os: string;
  status: string;
  lastSeen: string;
  ip: string;
  installed_apps: InstalledApp[];
  battery_level?: number | null;
  battery_status?: string | null;
  serial_number?: string | null;
  department?: string;
  location?: string;
}

const DeviceCard = ({ device }: { device: Device }) => {
  const navigate = useNavigate();

  const triggerHeartbeat = async () => {
    try {
      await devicesApi.checkInDevice({ deviceId: device.id } as any);
      toast.success('Device check-in successful');
    } catch (error) {
      console.error('Error in triggerHeartbeat:', error);
      toast.error('Failed to trigger device heartbeat');
    }
  };

  const handleUnenroll = async () => {
    try {
      await devicesApi.bulkUnenroll({ deviceIds: [device.id] });
      toast.success('Device unenrolled successfully');
    } catch (error) {
      console.error('Error unenrolling device:', error);
      toast.error('Failed to unenroll device');
    }
  };

  return (
    <GlassCard variant="default" className="p-6">
      <DeviceCardHeader
        name={device.name}
        status={device.status}
        onCheckIn={triggerHeartbeat}
        onUnenroll={handleUnenroll}
      />
      <DeviceCardInfo
        os={device.os}
        osType={device.os.split(' ')[0]}
        deviceName={device.name}
        lastSeen={device.lastSeen}
        ip={device.ip}
        serialNumber={device.serial_number}
        batteryLevel={device.battery_level}
        batteryStatus={device.battery_status}
        installedApps={device.installed_apps}
      />
      
      {(device.department || device.location) && (
        <div className="mt-4 pt-4 border-t border-border space-y-2">
          {device.department && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Users className="h-4 w-4 mr-2" />
              {device.department}
            </div>
          )}
          {device.location && (
            <div className="flex items-center text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 mr-2" />
              {device.location}
            </div>
          )}
        </div>
      )}
      
      <div className="mt-4">
        <Button variant="outline" className="w-full" onClick={() => navigate(`/devices/${device.id}`)}>
          View Details
        </Button>
      </div>
    </GlassCard>
  );
};

export default DeviceCard;
